import { QueryObserverResult, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import { getEventGraph, getEventGraphEdgeTasks } from './event-graph-client';
import type { EventGraph } from './event-graph-client.type';

/**
 * React Query hook to fetch event graph data.
 *
 * This hook retrieves event graph information based on provided filters like team, board, task type,
 * date range, and statuses. It formats the dates and statuses before making the API request.
 *
 * @param {Object} params - The parameters for filtering event graph data
 * @param {string|null} params.teamId - The ID of the team to filter by
 * @param {string|null} params.boardId - The ID of the board to filter by
 * @param {string|null} params.taskType - The type of tasks to filter by
 * @param {string|null} params.epicId - The ID of the epic to filter by
 * @param {string|null} params.workflowId - The ID of the workflow to filter by
 * @param {string} params.startDate - The start date for the date range filter
 * @param {string} params.endDate - The end date for the date range filter
 * @param {string[]} params.statuses - Array of status strings to filter by
 *
 * @returns {Object} The event graph data and query result
 * @returns {EventGraph|undefined} .eventGraph - The event graph data if available
 * @returns {QueryObserverResult<EventGraph>} .query - The React Query result object
 */
const useEventGraph = ({
  teamId,
  boardId,
  taskType,
  epicId,
  taskId,
  workflowId,
  startDate,
  endDate,
  statuses,
}: {
  teamId: string | null;
  boardId: string | null;
  taskType: string | null;
  epicId: string | null;
  taskId: string | null;
  workflowId: string | null;
  startDate: string;
  endDate: string;
  statuses: string[];
}): { eventGraph: EventGraph | undefined; query: QueryObserverResult<EventGraph> } => {
  const formattedStatuses = statuses.map((status) => status.toLowerCase()).join(',');

  const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
  const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');

  const query = useQuery({
    queryKey: [
      'event-graph',
      teamId,
      boardId,
      taskType,
      epicId,
      taskId,
      workflowId,
      formattedStartDate,
      formattedEndDate,
      formattedStatuses,
    ],
    queryFn: () =>
      getEventGraph({
        project_id: teamId,
        board_id: boardId,
        task_type: taskType,
        epic_id: epicId,
        task_id: taskId,
        workflow_id: workflowId,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        statuses: formattedStatuses,
      }),
    enabled: !!teamId || !!workflowId,
  });

  return { eventGraph: query.data, query };
};

/**
 * React Query hook to fetch tasks associated with a specific edge in the event graph.
 *
 * This hook retrieves task records that are related to a given edge ID, filtered by the provided parameters.
 * It formats the dates and statuses before making the API request.
 *
 * @param {Object} params - The parameters for filtering edge-related tasks
 * @param {string|null} params.teamId - The ID of the team to filter by
 * @param {string|null} params.boardId - The ID of the board to filter by
 * @param {string|null} params.taskType - The type of tasks to filter by
 * @param {string|null} params.epicId - The ID of the epic to filter by
 * @param {string|null} params.workflowId - The ID of the workflow to filter by
 * @param {string} params.startDate - The start date for the date range filter
 * @param {string} params.endDate - The end date for the date range filter
 * @param {string[]} params.statuses - Array of status strings to filter by
 * @param {string} params.edgeId - The ID of the edge to fetch tasks for
 *
 * @returns {Object} The tasks and query result
 * @returns {TaskRecord[]|undefined} .tasks - The array of task records if available
 * @returns {QueryObserverResult<TaskRecord[]>} .query - The React Query result object
 */
const useEventGraphEdgeTasks = ({
  teamId,
  boardId,
  taskType,
  epicId,
  taskId,
  workflowId,
  startDate,
  endDate,
  statuses,
  edgeId,
}: {
  teamId: string | null;
  boardId: string | null;
  taskType: string | null;
  epicId: string | null;
  taskId: string | null;
  workflowId: string | null;
  startDate: string;
  endDate: string;
  statuses: string[];
  edgeId: string;
}): { tasks: TaskRecord[] | undefined; query: QueryObserverResult<TaskRecord[]> } => {
  const formattedStatuses = statuses.map((status) => status.toLowerCase()).join(',');

  const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
  const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');

  const query = useQuery({
    queryKey: [
      'event-graph-edge-tasks',
      teamId,
      boardId,
      taskType,
      epicId,
      taskId,
      workflowId,
      formattedStartDate,
      formattedEndDate,
      formattedStatuses,
      edgeId,
    ],
    queryFn: () =>
      getEventGraphEdgeTasks({
        project_id: teamId,
        board_id: boardId,
        task_type: taskType,
        epic_id: epicId,
        task_id: taskId,
        workflow_id: workflowId,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        statuses: formattedStatuses,
        edge_id: edgeId,
      }),
    enabled: !!edgeId && (!!teamId || !!workflowId),
  });

  return { tasks: query.data, query };
};

export { useEventGraph, useEventGraphEdgeTasks };
